import * as React from 'react';
import { CurrentLocationProvider } from './providers/CurrentLocationProvider/CurrentLocationProvider';
import { FEATURE_FLAGS } from './components';
import { InstallPwa } from './InstallPwa';
import { ModalSuccess } from './components/ModalSuccess/ModalSuccess';
import { MyProperties } from './modules/Properties/MyProperties';
import {
  Navigate,
  useLocation,
  useNavigate,
  useRoutes,
} from 'react-router-dom';
import { OpeningScreen, PopUpSpecialist, UiProvider } from './components';
import { PropertyForm } from './modules/Properties/PropertyForm';
import { PropertyProfile } from './modules/Properties/PropertyProfile';
import { RegistrationProduction } from './modules/PropertiesProduction/RegistrationProduction';
import { SiflorApiProvider } from './providers/SiflorApiProvider/SiflorApiProvider';
import { useAuth } from '@ttoss/react-auth';
import Biophysics from './pages/Biophysics';
import Home from './pages/Home';
import Login from './pages/Login';
import MyAccountPage from './pages/MyAccount';
import MyPlaces from './pages/MyPlaces';
import Recommendations from './pages/Recommendations/Recommendations';
import Register from './pages/Register';
import SavedPropertiesPage from './pages/SavedProperties';
import SearchBuyerPersonPage from './pages/SearchBuyerPerson';
import SearchProviderPersonPage from './pages/SearchProviderPerson';
import TermsOfUsePage from './pages/TermsOfUse';
import Welcome from './pages/Welcome';

const AuthenticatedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();

  const navigate = useNavigate();

  return (
    <>
      <ModalSuccess
        isOpen={!isAuthenticated}
        message="Para acessar a ferramenta você precisa estar logado."
        buttonLabel="Login"
        onClickButton={() => {
          navigate('/login');
        }}
      />
      {isAuthenticated && <>{children}</>}
    </>
  );
};

const getRoutes = () => {
  const defaultRoutes: {
    path: string;
    element: React.ReactNode;
    authenticated?: boolean;
  }[] = [
    {
      path: '/',
      element: <Home />,
    },
    {
      path: '/recomendacoes',
      element: <Recommendations />,
    },
    {
      path: '/biofisico',
      element: <Biophysics />,
    },

    {
      path: '*',
      element: <Navigate to="/" />,
    },
  ];

  if (FEATURE_FLAGS.USER_MODULE) {
    defaultRoutes.push({
      path: '/welcome',
      element: <Welcome />,
    });

    defaultRoutes.push({
      path: '/login',
      element: <Login />,
    });

    defaultRoutes.push({
      path: '/cadastro',
      element: <Register />,
    });

    defaultRoutes.push({
      path: '/termos',
      element: <TermsOfUsePage />,
    });

    defaultRoutes.push({
      path: '/minha-conta',
      authenticated: true,
      element: <MyAccountPage />,
    });
  }

  if (FEATURE_FLAGS.PLACES_MODULE) {
    defaultRoutes.push({
      path: '/meus-locais',
      authenticated: true,
      element: <MyPlaces />,
    });
  }

  if (FEATURE_FLAGS.PROPERTIES_MODULE) {
    defaultRoutes.push({
      path: '/cadastro-propriedade',
      authenticated: true,
      element: <PropertyForm />,
    });

    defaultRoutes.push({
      path: '/propriedade/:propertyId',
      authenticated: true,
      element: <PropertyProfile />,
    });

    defaultRoutes.push({
      path: '/propriedade/:propertyId/editar',
      authenticated: true,
      element: <PropertyForm />,
    });

    defaultRoutes.push({
      path: '/minhas-propriedades',
      authenticated: true,
      element: <MyProperties />,
    });

    defaultRoutes.push({
      path: '/propriedades-salvas',
      authenticated: true,
      element: <SavedPropertiesPage />,
    });
  }

  if (FEATURE_FLAGS.PROPERTIES_PRODUCTION_MODULE) {
    defaultRoutes.push({
      path: '/propriedade/:propertyId/cadastro-producao',
      authenticated: true,
      element: <RegistrationProduction />,
    });

    defaultRoutes.push({
      path: '/propriedade/:propertyId/producao/:propertyProductionId/editar',
      authenticated: true,
      element: <RegistrationProduction />,
    });
  }

  if (FEATURE_FLAGS.SEARCH_MODULE) {
    defaultRoutes.push({
      path: '/busca-comprador',
      authenticated: true,
      element: <SearchBuyerPersonPage />,
    });

    defaultRoutes.push({
      path: '/busca-fornecedor',
      authenticated: true,
      element: <SearchProviderPersonPage />,
    });
  }

  return defaultRoutes.map((route) => {
    if (route.authenticated) {
      return {
        ...route,
        element: <AuthenticatedRoute>{route.element}</AuthenticatedRoute>,
      };
    }

    return route;
  });
};

const App = () => {
  const { signOut } = useAuth();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const showPopUpSpecialist = ['/', '/recomendacoes', '/biofisico'].includes(
    pathname
  );

  const routes = useRoutes(getRoutes());

  return (
    <SiflorApiProvider>
      <UiProvider
        onNavigate={(menu) => {
          if (menu === 'recommendation') {
            return navigate('/recomendacoes');
          }

          if (menu === 'biophysics') {
            return navigate('/biofisico');
          }
        }}
        onLogout={() => {
          signOut();
          navigate('/');
        }}
      >
        <CurrentLocationProvider>
          <React.Suspense fallback={null}>{routes}</React.Suspense>
        </CurrentLocationProvider>
        <OpeningScreen />
        {showPopUpSpecialist && <PopUpSpecialist />}
        <InstallPwa />
      </UiProvider>
    </SiflorApiProvider>
  );
};

export default App;
